import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import styles from "./NewAppPage.module.scss";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";
import CustomInput from "../../components/Inputs/CustomInput";
import CustomCheckbox from "../../components/CustomCheckbox/CustomCheckbox";
import Modal from "../../components/Modal/Modal";
import { AxiosErrorType } from "../../types";

interface App {
  name: string;
  description?: string;
  isActive?: boolean;
}

export default function NewAppPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<App>({
    name: "",
    description: "",
    isActive: true,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [modalIsError, setModalIsError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!formData.name) {
      setModalMessage("Please provide a name for the app.");
      setModalIsError(true);
      setShowModal(true);
      return;
    }

    setLoading(true);
    try {
      console.log("[NewAppPage] Creating app:", {
        name: formData.name,
        description: formData.description || null,
        isActive: formData.isActive,
      });

      const response = await api.post("/core/app/create", {
        name: formData.name,
        description: formData.description || null,
        isActive: formData.isActive,
      });

      console.log("[NewAppPage] App created:", response.data);
      setModalMessage("App created successfully.");
      setModalIsError(false);
      setShowModal(true);
      setTimeout(() => navigate("/apps"), 2000);
    } catch (err: AxiosErrorType) {
      const errorMessage =
        err.response?.status === 429
          ? "Too many requests, please try again later."
          : err.response?.data?.message || "Failed to create app";
      console.error("[NewAppPage] Failed to create app:", err.message, err.response?.data);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
      if (err.response?.status === 401) {
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      label: "Details",
      content: (
        <div className={styles.formGrid}>
          <CustomInput
            label="Name"
            value={formData.name || ""}
            onChange={(val) => setFormData({ ...formData, name: val })}
            type="text"
            required
            size="full"
          />
          <CustomInput
            label="Description"
            value={formData.description || ""}
            onChange={(val) => setFormData({ ...formData, description: val })}
            type="text"
            size="full"
          />
          <CustomCheckbox
            label="Active"
            checked={formData.isActive ?? true}
            onChange={(val) => setFormData({ ...formData, isActive: val })}
            id="isActive"
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>Create New App</div>
      <TabbedFormLayout
        tabs={tabs}
        onSave={handleSubmit}
        onCancel={() => navigate("/apps")}
        showActions={true}
        disabled={loading}
      />
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        message={modalMessage}
        isError={modalIsError}
      />
    </div>
  );
}
